<script setup lang="ts">
import { reactive, ref } from "vue";
import { useModal } from "#/form/useModal";
import { useAccount, DuplicateEmailResponseStatus } from "#/account/useAccount";
import { notification } from "#/utils/notification";
import FormInput from "#/form/FormInput.vue";
import StepCode from "#/account/email/AddEmailStepCode.vue";

interface Props {
  email?: string;
  modalId?: string;
  type: "applicant" | "employer";
}

const props = defineProps<Props>();

const {
  changeApplicantEmail,
  state,
  cancelChangeEmail,
  unlinkEmail,
  approveVerificationCode,
} = useAccount();
const { close } = useModal();
const emailInput = ref("");
const errorMessage = ref("");

const steps = reactive({
  isFistStep: true,
  emailHasAnotherApplicant: false,
  emailHasAnotherEmployer: false,
  codeStep: false,
});

const clearSteps = () => {
  let i: keyof typeof steps;
  for (i in steps) {
    steps[i] = false;
  }
};

const approve = async (codeInput: string) => {
  try {
    await approveVerificationCode(codeInput);

    notification.success("Email успешно изменен");

    window.location.reload();
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const changeEmailFromCodeStep = async () => {
  try {
    await cancelChangeEmail();
    changeStep("isFistStep");
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const addEmail = async () => {
  try {
    const response = await changeApplicantEmail(emailInput.value);

    if (response === DuplicateEmailResponseStatus.Success) {
      changeStep("codeStep");
    }

    if (response === DuplicateEmailResponseStatus.DuplicateApplicant) {
      changeStep("emailHasAnotherApplicant");
    }

    if (response === DuplicateEmailResponseStatus.DuplicateEmployer) {
      changeStep("emailHasAnotherEmployer");
    }
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const cancel = async () => {
  try {
    await cancelChangeEmail();
    closeModal();
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const unlink = async () => {
  try {
    await unlinkEmail();
    changeStep("codeStep");
  } catch (e: any) {
    errorMessage.value = e;
  }
};

const closeModal = () => {
  clearSteps();
  steps.isFistStep = true;
  emailInput.value = "";

  if (props.modalId) close(props.modalId);
};

const changeStep = (step: keyof typeof steps) => {
  errorMessage.value = "";
  clearSteps();
  steps[step] = true;
};
</script>

<template>
  <div v-if="steps.isFistStep">
    <span class="color-dark flex mb-20 text-small justify-center">
      Отправим код на указанную почту, чтобы подтвердить аккаунт
    </span>

    <form-input
      v-model="emailInput"
      label="Новый e-mail"
      placeholder="your@mail.ru"
      class="mb-24"
      @keyup.enter="addEmail"
    />

    <span v-if="errorMessage" class="flex mb-24 color-error">{{
      errorMessage
    }}</span>

    <button
      type="submit"
      class="w-full flex justify-center counter-form__submit btn"
      :class="state.isLoading ? 'tag-disable' : 'tag-primary'"
      :disabled="state.isLoading"
      @click="addEmail"
    >
      Добавить почту к аккаунту
    </button>
  </div>

  <div v-if="steps.emailHasAnotherApplicant" class="mt-20">
    <span class="color-dark d-block mb-44 text-small text-center">
      Данная почта привязана к другому аккаунту на нашем сайте. Обратите
      внимание, что вы можете <span class="text-bold">потерять доступ</span> к
      прошлому аккаунту, если перенесете e-mail. Продолжить добавление?
    </span>

    <div class="flex justify-between flex-gap-20">
      <button
        class="w-full tag-disable flex justify-center counter-form__submit btn"
        @click="cancel"
      >
        Отменить
      </button>
      <button
        type="submit"
        class="w-full tag-primary flex justify-center counter-form__submit btn"
        @click="unlink"
      >
        Продолжить
      </button>
    </div>
  </div>

  <div v-if="steps.emailHasAnotherEmployer" class="mt-20">
    <span class="color-dark flex mb-44 text-small text-center justify-center">
      Данная почта привязана к аккаунту работодателя на нашем сайте.
      Чтобы привязать почту к аккаунту, авторизуйтесь как работодатель и отвяжите почту
    </span>

    <button
      type="submit"
      class="w-full tag-disable flex justify-center counter-form__submit btn"
      @click="cancel"
    >
      Отменить привязку
    </button>
  </div>

  <div v-if="steps.codeStep">
    <step-code
      :email="emailInput"
      :is-loading="state.isLoading"
      :error-message="errorMessage"
      @error:update-message="errorMessage = $event"
      @error:clear-message="errorMessage = ''"
      @change-email="changeEmailFromCodeStep"
      @form:approve="approve"
    />
  </div>
</template>
